import { useState, useEffect, useCallback } from 'react';
import { SOUNDS } from '../config/prizeConfig';

// Global audio state stored in localStorage for persistence across components
const AUDIO_STORAGE_KEY = 'kbc_audio_enabled';

const getInitialAudioState = (): boolean => {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(AUDIO_STORAGE_KEY);
  return stored === null ? true : stored === 'true';
};

const setAudioState = (enabled: boolean) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(AUDIO_STORAGE_KEY, String(enabled));
    // Dispatch custom event to sync across all useAudio instances
    window.dispatchEvent(new CustomEvent('audioStateChange', { detail: { enabled } }));
  }
};

/**
 * Custom hook to manage audio playback for quiz sound effects
 * Audio state is shared globally across all instances via localStorage
 */
export const useAudio = () => {
  const [audioEnabled, setAudioEnabled] = useState(getInitialAudioState);
  const [audioElements, setAudioElements] = useState<Map<string, HTMLAudioElement>>(new Map());

  // Listen for audio state changes from other components
  useEffect(() => {
    const handleAudioStateChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ enabled: boolean }>;
      setAudioEnabled(customEvent.detail.enabled);
    };

    window.addEventListener('audioStateChange', handleAudioStateChange);
    return () => window.removeEventListener('audioStateChange', handleAudioStateChange);
  }, []);

  // Initialize audio elements
  useEffect(() => {
    const elements = new Map<string, HTMLAudioElement>();
    
    Object.entries(SOUNDS).forEach(([key, path]) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      audio.onerror = () => {
        console.warn(`Audio file not found: ${path}`);
      };
      elements.set(key, audio);
    });

    setAudioElements(elements);

    // Cleanup
    return () => {
      elements.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  /**
   * Play a sound effect
   * FAILSAFE: If audio is disabled, set volume to 0 instead of not playing
   */
  const playSound = useCallback((soundKey: keyof typeof SOUNDS) => {
    const audio = audioElements.get(soundKey);
    if (audio) {
      audio.currentTime = 0;
      // FAILSAFE: Set volume to 0 if disabled (should not be needed, but acts as backup)
      audio.volume = audioEnabled ? 1.0 : 0;
      
      if (!audioEnabled) {
        console.debug('Audio disabled, skipping playback');
        return;
      }
      
      audio.play().catch(err => {
        // Silently fail if audio can't play (e.g., user hasn't interacted with page yet)
        console.debug('Audio play failed:', err);
      });
    }
  }, [audioEnabled, audioElements]);

  /**
   * Stop all sounds
   */
  const stopAllSounds = useCallback(() => {
    audioElements.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
  }, [audioElements]);

  /**
   * Toggle audio on/off
   */
  const toggleAudio = useCallback(() => {
    const newState = !audioEnabled;
    setAudioEnabled(newState);
    setAudioState(newState);
  }, [audioEnabled]);

  return {
    audioEnabled,
    audioMuted: !audioEnabled,  // Alias for compatibility with components
    playSound,
    stopAllSounds,
    toggleAudio,
  };
};

